/**
 * SignalGauge — half-circle gauge with segmented arc (green/amber/red).
 * value: 0..100 — where the needle points.
 * status: 'green' | 'amber' | 'red' — colors the readout.
 */
export default function SignalGauge({
  label = "Capacity",
  value = 62,
  status = "amber",
  unit = "%",
  hint = "",
  testId = "gauge",
}) {
  const clamped = Math.max(0, Math.min(100, value));
  const angle = -90 + (clamped / 100) * 180; // -90deg (left) to +90deg (right)

  const statusColor =
    status === "green"
      ? "text-signalGreen"
      : status === "red"
        ? "text-signalRed"
        : "text-signalAmber";

  const statusLabel =
    status === "green" ? "STABLE" : status === "red" ? "OVERLOAD" : "COMPRESSED";

  return (
    <div
      data-testid={testId}
      className="relative flex flex-col items-center bg-deep border border-rule p-6"
    >
      {/* label row */}
      <div className="flex w-full items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-mist">
          {label}
        </span>
        <span className={`font-mono text-[10px] tracking-[0.2em] ${statusColor}`}>
          {statusLabel}
        </span>
      </div>

      {/* gauge */}
      <div className="relative mt-4 h-24 w-full max-w-[220px]">
        <svg viewBox="0 0 220 120" className="h-full w-full">
          {/* base arc */}
          <path
            d="M 20 110 A 90 90 0 0 1 200 110"
            fill="none"
            stroke="#192A45"
            strokeWidth="10"
            strokeLinecap="butt"
          />
          {/* green segment 0-45% */}
          <path
            d="M 20 110 A 90 90 0 0 1 79 30"
            fill="none"
            stroke="#4E8B7C"
            strokeWidth="10"
            opacity="0.85"
          />
          {/* amber segment 45-75% */}
          <path
            d="M 79 30 A 90 90 0 0 1 155 40"
            fill="none"
            stroke="#D98A44"
            strokeWidth="10"
            opacity="0.85"
          />
          {/* red segment 75-100% */}
          <path
            d="M 155 40 A 90 90 0 0 1 200 110"
            fill="none"
            stroke="#B75555"
            strokeWidth="10"
            opacity="0.85"
          />
          {/* tick marks */}
          {Array.from({ length: 11 }).map((_, i) => {
            const a = (Math.PI * (180 - i * 18)) / 180;
            const x1 = 110 + Math.cos(a) * 78;
            const y1 = 110 - Math.sin(a) * 78;
            const x2 = 110 + Math.cos(a) * 70;
            const y2 = 110 - Math.sin(a) * 70;
            return (
              <line
                key={i}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="#F4F1ED"
                strokeOpacity="0.3"
                strokeWidth="1"
              />
            );
          })}
          {/* needle */}
          <g transform={`rotate(${angle} 110 110)`}>
            <line
              x1="110"
              y1="110"
              x2="110"
              y2="30"
              stroke="#F4F1ED"
              strokeWidth="1.5"
            />
            <circle cx="110" cy="110" r="4" fill="#F4F1ED" />
          </g>
        </svg>
      </div>

      {/* readout */}
      <div className="mt-2 flex items-baseline gap-1">
        <span className={`font-mono text-3xl ${statusColor}`}>{clamped}</span>
        <span className="font-mono text-sm text-mist">{unit}</span>
      </div>

      {hint && (
        <p className="mt-3 text-center text-xs leading-relaxed text-mist">{hint}</p>
      )}
    </div>
  );
}
